import { addDays, format, isSameDay, startOfDay } from "date-fns";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const CALENDAR_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

const localDateFromParts = ([, year, month, day]: RegExpExecArray) =>
  new Date(Number(year), Number(month) - 1, Number(day));

/**
 * SERV-3074: Mantine 8 date pickers emit a bare "yyyy-MM-dd" string. `new Date(thatString)` is UTC
 * midnight, so `format(..., "yyyy-MM-dd")` on it reads the day before in every US timezone. This
 * reads it as local midnight of the picked day instead. A Date (or a string with a time) passes
 * through the plain constructor as before.
 */
export const parsePickerDate = (value: string | Date): Date => {
  if (value instanceof Date) return new Date(value.getTime());
  const dateOnly = DATE_ONLY_PATTERN.exec(value);
  return dateOnly ? localDateFromParts(dateOnly) : new Date(value);
};

/**
 * A bare "2026-09-20" is a calendar day, but the Date constructor reads it as UTC midnight, which
 * is the previous day in every US timezone. Read it as a local date instead, the same rule as
 * `parseTravelDate` (Procurement/shared/flightBooking.ts). Anything with a time parses as before.
 */
export const formatDateToMMDDYYYY = (date: string) => {
  if (!date) return "";
  const dateOnly = DATE_ONLY_PATTERN.exec(date);
  return format(dateOnly ? localDateFromParts(dateOnly) : new Date(date), "MM/dd/yyyy");
};

/**
 * For `@db.Date` columns only. Prisma serialises those as UTC midnight ("2026-09-20T00:00:00.000Z"),
 * which `formatDateToMMDDYYYY` must keep treating as an instant, because a real timestamp can land
 * on UTC midnight too. The column holds a calendar day, so take the stored day and ignore the time.
 */
export const formatCalendarDateToMMDDYYYY = (date: string | null | undefined) => {
  if (!date) return "";
  const calendarDate = CALENDAR_DATE_PREFIX.exec(date);
  return calendarDate
    ? format(localDateFromParts(calendarDate), "MM/dd/yyyy")
    : formatDateToMMDDYYYY(date);
};

const STORED_CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/;

/**
 * SERV-2969: a picked calendar day is stored as UTC midnight of that day ("2026-09-01T00:00:00.000Z"),
 * which is what the backend buckets by. Takes the Date's LOCAL calendar day, the same normalisation
 * as `toUtcMidnight` (Procurement/design/rentalDuration.ts), because `toISOString` of a local-midnight
 * Date is the previous day east of UTC and 05:00Z in Chicago.
 */
export const toStoredCalendarDay = (date: Date): string =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();

/**
 * SERV-2969: the reading side of `toStoredCalendarDay`, for a calendar-day value held as a Date (a
 * picker seed, a same-day comparison). Takes the stored day from a bare "yyyy-MM-dd" or a UTC-midnight
 * ISO string and returns local midnight of it.
 */
export const parseStoredCalendarDate = (value: string | null | undefined): Date | null => {
  if (typeof value !== "string") return null;
  const calendarDate = CALENDAR_DATE_PREFIX.exec(value);
  if (!calendarDate) return null;
  const date = localDateFromParts(calendarDate);
  // An impossible day ("2026-02-30") rolls over in the Date constructor; treat it as no date.
  return date.getMonth() === Number(calendarDate[2]) - 1 &&
    date.getDate() === Number(calendarDate[3])
    ? date
    : null;
};

/**
 * SERV-2969: true for a bare "yyyy-MM-dd" or exactly UTC midnight, the shape a picked calendar day is
 * stored in. For fields that hold either a day or a real instant (`expFulfillmentAt`). An instant can
 * land on 00:00:00.000Z too: the retired CreateRequest wizards' `combineDateTime` with 7:00 PM CDT
 * does, so those historical rows read as the next day with no time. No mounted writer produces one.
 */
export const isStoredCalendarDay = (value: string | null | undefined): boolean =>
  !!value && STORED_CALENDAR_DAY_PATTERN.test(value);

export function toValidDate(date?: Date | string | number | null): Date | null {
  if (!date) return null;

  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d;
}

export function isToday(date: Date): boolean {
  return isSameDay(startOfDay(date), startOfDay(new Date()));
}

export function isTomorrow(date: Date): boolean {
  return isSameDay(startOfDay(date), startOfDay(addDays(new Date(), 1)));
}

export function formatDateTime(
  date?: Date | string | number | null,
  showTime: boolean = true,
  showDate: boolean = true,
  ShowFullDate: boolean = false,
): string {
  const validDate = toValidDate(date);
  if (!validDate) return "";

  // If ShowFullDate is true, skip relative date labels (Today/Tomorrow)
  if (!ShowFullDate) {
    // TODAY
    if (isToday(validDate)) {
      if (showDate && showTime) return "Today " + format(validDate, "hh:mm a"); // Today 12:05 AM
      if (showDate && !showTime) return "Today"; // Today
      if (!showDate && showTime) return format(validDate, "hh:mm a"); // 12:05 AM
    }

    // TOMORROW
    if (isTomorrow(validDate)) {
      if (showDate && showTime) return "Tomorrow " + format(validDate, "hh:mm a"); // Tomorrow 12:05 AM
      if (showDate && !showTime) return "Tomorrow";
      if (!showDate && showTime) return format(validDate, "hh:mm a"); // 12:05 AM
    }
  }

  // NORMAL DATE (or ShowFullDate is true)
  if (showDate && showTime) {
    return format(validDate, "dd MMM, yyyy hh:mm a"); // 02 Dec, 2025 12:05 AM
  }

  if (showDate && !showTime) {
    return format(validDate, "dd MMM, yyyy"); // 02 Dec, 2025
  }

  if (!showDate && showTime) {
    return format(validDate, "hh:mm a"); // 12:05 AM
  }

  return "";
}

export const formatIsoDateToDisplayDateTime = (isoDate: string, timezone?: string | null) => {
  if (!isoDate) return "";
  const utcDate = new Date(isoDate);
  if (isNaN(utcDate.getTime())) return "";

  const date = timezone
    ? new Date(utcDate.toLocaleString("en-US", { timeZone: timezone }))
    : utcDate;

  const datePart = format(date, "d MMMM");
  const timePart = format(date, "hh:mm a");

  return `${datePart} | ${timePart}`;
};

/**
 * Converts a Date object or date string to UTC ISO format
 * @param date - Date object, string, or null
 * @returns ISO string in UTC format or undefined
 */
export function toUTCISOString(date?: Date | string | null): string | undefined {
  if (!date) return undefined;

  const validDate = toValidDate(date);
  if (!validDate) return undefined;

  return validDate.toISOString();
}
/**
 * Parse an ISO string as a local date using the UTC components directly,
 * so the calendar date/time matches exactly what the backend sent.
 */
export const parseUtcAsLocal = (isoString: string): Date => {
  const date = new Date(isoString);
  return date;
};

/**
 * Format an ISO date string to "YYYY-MM-DD" (local time).
 * Returns empty string if the input is invalid.
 */
export function formatToDateString(isoString: string): string {
  const date = toValidDate(isoString);
  if (!date) return "";
  const padZero = (number: number) => String(number).padStart(2, "0");
  const year = date.getFullYear();
  const month = padZero(date.getMonth() + 1);
  const day = padZero(date.getDate());
  return `${year}-${month}-${day}`;
}

/**
 * Format an ISO date string to "YYYY-MM-DD HH:mm:ss" (local time).
 * Returns empty string if the input is invalid.
 */
export function formatToDateTimeString(isoString: string): string {
  const date = toValidDate(isoString);
  if (!date) return "";
  const padZero = (number: number) => String(number).padStart(2, "0");
  const year = date.getFullYear();
  const month = padZero(date.getMonth() + 1);
  const day = padZero(date.getDate());
  const hours = padZero(date.getHours());
  const minutes = padZero(date.getMinutes());
  const seconds = padZero(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const formatDriveableTime = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);

  const hourText = hours ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const minuteText = minutes ? `${minutes} minute${minutes > 1 ? "s" : ""}` : "";

  return [hourText, minuteText].filter(Boolean).join(" ");
};

export function showDateAndTimeLine(date?: string | Date | null): string {
  const d = toValidDate(date);
  if (!d) return "";
  return `${format(d, "d MMMM, yyyy")} • ${format(d, "h:mm a").toLowerCase()}`;
}

/**
 * Minute granularity of the app's time-slot lists. Shared so that a prefilled value
 * always lands on a real slot instead of between two of them.
 */
export const DEFAULT_TIME_STEP_MINUTES = 15;

/**
 * The current local time, floored to `stepMinutes`, with seconds cleared.
 *
 * Flooring rather than rounding is deliberate: the value must never read as a time
 * that has not happened yet. Fields seeded with this are validated elsewhere as
 * "cannot be in the future", and rounding up would put one in breach on mount.
 *
 * SERV-2603: time fields default to the user's machine clock rather than to midnight.
 */
export function currentTimeSlotDate(stepMinutes: number = DEFAULT_TIME_STEP_MINUTES): Date {
  const step = stepMinutes > 0 ? stepMinutes : DEFAULT_TIME_STEP_MINUTES;
  const now = new Date();
  now.setMinutes(now.getMinutes() - (now.getMinutes() % step), 0, 0);
  return now;
}
